// includes/js-src/inventory/index.ts
//
// Entry point for the inventory renderer bundle (inventory→TS Phase 1, 2026-06-01).
// Carves renderInventoryThumbnail out of the template.js god-file into a typed esbuild
// IIFE and re-exposes it as window.renderInventoryThumbnail so the existing ambient-global
// callers (encounter-update WS and trade-trainer-init.js) keep working
// unchanged. Loaded BEFORE list-display / encounter-map / template.js; renderInventoryThumbnail
// is only ever *invoked* at runtime, by which point its ambient deps (item-arr-readers.js,
// core-helpers.js, template.js inline catalogs) are defined.
//
// Equipment-slot grid + live-caster-sheet renderers land here in Phases 2–4.

import { renderInventoryThumbnail, syncInventoryTokenThumbMediaIfChanged, inventoryMapTokenMediaSignature } from './thumbnail';
import { renderEquipmentSlotGrid, patchEquipmentSlotGrid } from './equipment-grid';
import { installInventoryActivityListeners } from './don-doff-overlay';
import { claimInventoryInstance, inventoryInstanceRootRequest } from '../shared/inventory-instance-root';
import { paintUnsavedPreviewTiles, unsavedInventoryPreview } from './unsaved-preview';
import type { PendingPreviewTile } from './unsaved-preview';

installInventoryActivityListeners();

// Inventory map-token media hot-swap (analogue of syncCreatureTokenThumbMediaIfChanged): animate-entity.ts
// calls this on every inventory animateEntityChanges pass so a floor item's picture repaints surgically
// from the encounterArr blob (no full XZ widget rebuild). Window-attached because animate-entity.ts lives
// in a different bundle and reaches it as an ambient global.
// Equipment-slot grid (Phase 2): the list-display bundle calls this instead of innerHTML-ing the
// old server-captured ft-ld-ts-equip HTML. Takes the compiled blob, returns the grid HTML.
Object.assign(window, {
    renderInventoryThumbnail,
    syncInventoryTokenThumbMediaIfChanged,
    inventoryMapTokenMediaSignature,
    renderEquipmentSlotGrid,
    patchEquipmentSlotGrid,
});

// -------------------------------------------------------------------------------------------------
// ftMountInventoryTiles — hydrator for the off-map tile mount (ft_inventory_tile_mount() PHP emitter).
// Mirrors ftMountThumbnails (includes/js-src/thumbnail/index.ts): PHP emits
//   <div class="ft-inv-tile-mount"><script type="application/json" class="ft-inv-tile-blob">
//     {"inventory":{…compiled arr…},"options":{…}}</script></div>
// The JSON-in-a-script survives innerHTML insertion (AJAX panels). The scanner reads the blob, renders
// via renderInventoryThumbnail, and REPLACES the whole .ft-inv-tile-mount wrapper with the tile — so the
// resulting DOM is the bare <div id="inventory_…"> the old PHP render_inventory_thumbnail() emitted,
// with no extra wrapper to perturb flex/grid layout. Idempotent: once replaced it's no longer a mount.
//
// TIMING (ordering trap): renderInventoryThumbnail's ambient deps (SIZE_TYPES / VISUAL_STATUS_EFFECTS /
// item-arr-readers.js / template.js inline catalogs) load LATER in the footer than this bundle, so the
// page-load self-run must wait for DOMContentLoaded (every footer script executed), NOT bundle-execute
// time. (ftMountThumbnails can self-run early only because its bundle loads after template.js.)
//
// AJAX re-hydration is by EXPLICIT scoped re-invocation — ftMountInventoryTiles(scope) next to the
// existing ftMountThumbnails calls — deliberately NOT a body-wide MutationObserver.
// -------------------------------------------------------------------------------------------------
function htmlToTileNode(html: string): HTMLElement | null {
    const tpl = document.createElement('template');
    tpl.innerHTML = (html || '').trim();
    return tpl.content.firstElementChild as HTMLElement | null;
}

function ftMountInventoryTiles(root?: Element | Document): void {
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('.ft-inv-tile-mount:not([data-inv-tile-mounted])');
    const previews: PendingPreviewTile[] = [];
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-inv-tile-mounted', '1'); // mark first so a thrown render never loops on rescan
        const blob = el.querySelector('script.ft-inv-tile-blob');
        if (!blob) continue;
        try {
            const raw: unknown = JSON.parse(blob.textContent || 'null');
            const data = raw !== null && typeof raw === 'object' && !Array.isArray(raw)
                ? raw as { inventory?: unknown; options?: unknown }
                : null;
            if (!data?.inventory) continue;
            const options = data.options !== null && typeof data.options === 'object' && !Array.isArray(data.options)
                ? data.options
                : {};
            // A saved instance arrives as a SELECT shell; its tile is its possessor's compiled node.
            const instance = inventoryInstanceRootRequest(data.inventory);
            if (instance) {
                claimInventoryInstance(instance, el, {
                    pending: mount => { mount.textContent = ''; },
                    node: (node, mount) => {
                        const tile = htmlToTileNode(renderInventoryThumbnail(node, {}, options));
                        if (tile) mount.replaceWith(tile);
                        return false; // the tile is static once placed, as the PHP tile was
                    },
                    refusal: (reason, mount) => { mount.textContent = reason; },
                });
                continue;
            }
            // A row with no saved record names its authored definition; the scan reads them in one batch below.
            const preview = unsavedInventoryPreview(data.inventory);
            if (preview) {
                el.textContent = '';
                previews.push({ mount: el, preview, render: row => {
                    const tile = htmlToTileNode(renderInventoryThumbnail(row, {}, options));
                    if (tile) el.replaceWith(tile);
                } });
                continue;
            }
            if (typeof data.inventory === 'object' && data.inventory !== null && '_inventory_refusal' in data.inventory) {
                el.textContent = String(data.inventory._inventory_refusal);
                continue;
            }
            const node = htmlToTileNode(renderInventoryThumbnail(data.inventory, {}, options));
            if (node) el.replaceWith(node); // drop the wrapper; the bare tile takes its place in flow
        } catch (e) {
            console.warn('ftMountInventoryTiles: mount failed', e);
        }
    }
    void paintUnsavedPreviewTiles(previews);
}

Object.assign(window, { ftMountInventoryTiles });

// Page-load self-run AFTER the ambient catalogs exist. At footer-execute time readyState is 'loading'
// (parser still active, template.js below us not yet run) → wait for DOMContentLoaded. If the bundle is
// (re)loaded after parsing (AJAX), everything is already defined → run now.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ftMountInventoryTiles());
} else {
    ftMountInventoryTiles();
}

declare global { interface Window { patchEquipmentSlotGrid?: typeof patchEquipmentSlotGrid } }
